import { isQuestionnaireNotFound } from "@/services/api/questionnaireQueryPolicy"
import { useGetQuestionnaireStatus } from "@/services/api/questionnaireService"
import { QuestionnaireStatus } from "@/types/questionnaire/models"
import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useRef, useState } from "react"

interface UseQuestionnairePollingProps {
  questionnaireId?: string
  enabled?: boolean
  onStatusChange?: (status: QuestionnaireStatus) => void
  onExtracted?: () => void
  onFailed?: (error: Error) => void
}

export const useQuestionnairePolling = ({
  questionnaireId,
  enabled = true,
  onStatusChange,
  onExtracted,
  onFailed,
}: UseQuestionnairePollingProps) => {
  const queryClient = useQueryClient()
  const [currentStatus, setCurrentStatus] =
    useState<QuestionnaireStatus | null>(null)
  const [pollingError, setPollingError] = useState<Error | null>(null)
  const [missingQuestionnaireId, setMissingQuestionnaireId] = useState<
    string | null
  >(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const previousQuestionnaireId = useRef(questionnaireId)

  useEffect(() => {
    if (previousQuestionnaireId.current !== questionnaireId) {
      previousQuestionnaireId.current = questionnaireId
      setCurrentStatus(null)
      setPollingError(null)
      setMissingQuestionnaireId(null)
    }
  }, [questionnaireId])

  const shouldPoll =
    enabled &&
    questionnaireId &&
    questionnaireId !== missingQuestionnaireId &&
    (currentStatus === QuestionnaireStatus.New ||
      currentStatus === QuestionnaireStatus.Extracting ||
      currentStatus === null)

  const {
    data: statusData,
    error,
    refetch,
  } = useGetQuestionnaireStatus(questionnaireId, false)

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const startPolling = useCallback(() => {
    if (!intervalRef.current && shouldPoll) {
      refetch()
      intervalRef.current = setInterval(() => {
        refetch()
      }, 3000)
    }
  }, [questionnaireId, shouldPoll, refetch])

  useEffect(() => {
    if (shouldPoll) {
      startPolling()
    } else {
      stopPolling()
    }

    return () => {
      stopPolling()
    }
  }, [shouldPoll, startPolling, stopPolling])

  useEffect(() => {
    if (statusData?.status) {
      const newStatus = statusData.status as QuestionnaireStatus

      if (newStatus !== currentStatus) {
        setCurrentStatus(newStatus)
        onStatusChange?.(newStatus)

        if (newStatus === QuestionnaireStatus.Extracted) {
          stopPolling()
          // Invalidate questionnaire query to trigger refetch with updated status
          queryClient.invalidateQueries({
            queryKey: ["questionnaire", { questionnaireId }],
          })
          onExtracted?.()
        } else if (newStatus === QuestionnaireStatus.Failed) {
          stopPolling()
          const failError = new Error("Questionnaire processing failed")
          setPollingError(failError)
          onFailed?.(failError)
        }
      }
    }
  }, [
    statusData,
    currentStatus,
    onStatusChange,
    onExtracted,
    onFailed,
    stopPolling,
    queryClient,
    questionnaireId,
  ])

  useEffect(() => {
    if (error) {
      stopPolling()
      if (isQuestionnaireNotFound(error) && questionnaireId) {
        setMissingQuestionnaireId(questionnaireId)
      }
      const errorObj = error instanceof Error ? error : new Error(String(error))
      setPollingError(errorObj)
      onFailed?.(errorObj)
    }
  }, [error, onFailed, stopPolling, questionnaireId])

  return {
    currentStatus,
    isPolling: !!intervalRef.current,
    pollingError,
    statusData,
  }
}
